import Node from 'nanogl-node'

import { RunnerActor } from '@/state/RunnerStateMachine'
import { RenderContext } from '@/webgl/core/RenderContext'
import { IScene } from '@/webgl/engine/IScene'
import Renderer from '@/webgl/Renderer'
import GameWorld from '@/webgl/runner/GameWorld'
import { IRunnerRenderer } from '@/webgl/runner/interfaces/IRunnerRenderer'
import RunnerCamera from '@/webgl/runner/RunnerCamera'
import ScenarioAudio from '@/webgl/runner/scenarios/ScenarioAudio'
import { AudioSegment } from '@/webgl/runner/scenarios/segments/AudioSegment'
import Countdown from '@/webgl/runner/scenarios/segments/Countdown'
import { Intro } from '@/webgl/runner/scenarios/segments/Intro'
import MilkSegment from '@/webgl/runner/scenarios/segments/MilkSegment'
import { Outro } from '@/webgl/runner/scenarios/segments/Outro'
import { PlaySegment } from '@/webgl/runner/scenarios/segments/PlaySegment'
import { Popin } from '@/webgl/runner/scenarios/segments/Popin'
import { TransitionSegment } from '@/webgl/runner/scenarios/segments/TransitionSegment'
import { TurningPoint } from '@/webgl/runner/scenarios/segments/TurningPoint'
import RunnerRenderer from '@/webgl/runner/SceneRenderer'
import PostprocessSystem from '@/webgl/runner/systems/PostprocessSystem'
import SequencingSystem from '@/webgl/runner/systems/SequencingSystem'

import Tooltip from '../segments/Tooltip'
import SETUP from './Pedro1Setup'

export default class Pedro1Scene implements IScene {
  glRenderer: Renderer
  root: Node
  camCtrl: RunnerCamera

  world: GameWorld

  renderer: IRunnerRenderer

  audio: ScenarioAudio

  constructor(renderer: Renderer) {
    this.glRenderer = renderer

    this.root = new Node()
    this.world = new GameWorld(this.root)
    this.camCtrl = new RunnerCamera(this.world)

    this.renderer = new RunnerRenderer(renderer, this.world, SETUP)

    this.world.getTrackingSystem().useDomElement(this.glRenderer.ilayer)

    this.world.registry.init(SETUP)
    this.world.systems.add(new PostprocessSystem(this.world, this.glRenderer.postprocess))

    this.audio = new ScenarioAudio('en', SETUP.audio, SETUP.audioSfx)

    this.world.background.spawnableItems = SETUP.sequences[0].scrolling

    const sequencing = new SequencingSystem(this.world)
    this.world.systems.add(sequencing)

    sequencing
      .init(this.renderer, SETUP)
      .add(new Intro('introduction'))
      .add(new AudioSegment('audio_intro'))
      .add(new Popin('popin1', true))
      .add(new Countdown('countdown', 3))
      .add(new Tooltip('move'))
      .add(new Tooltip('pause'))
      .add(new Tooltip('watch'))
      .add(new PlaySegment('bathroom'))
      .add(new TransitionSegment('transition_bathroom_end'))
      .add(new AudioSegment('audio_dress_transition'))
      .add(new Popin('popin2'))
      .add(new PlaySegment('dressed_1'))
      .add(new PlaySegment('dressed_2'))
      .add(new TurningPoint('television'))
      .add(new PlaySegment('dressed_3'))
      .add(new PlaySegment('dressed_4'))
      .add(new TurningPoint('sock'))
      .add(new PlaySegment('dressed_socks'))
      .add(new TurningPoint('dog'))
      .add(new TransitionSegment('transition_breakfast'))
      .add(new Popin('popin3'))
      .add(new PlaySegment('breakfast'))
      .add(new PlaySegment('breakfast_milk', new MilkSegment('milk')))
      // .add(new TurningPoint("squirrel"))
      .add(new AudioSegment('audio_mother_help'))
      .add(new PlaySegment('breakfast_end'))
      .add(new AudioSegment('audio_outro'))
      .add(new Outro('outro'))
    // .add(new TransitionSegment("transition_end"))

    /// #if _DEBUG
    const params = new URLSearchParams(window.location.search)
    if (params.get('segment')) {
      sequencing.setStartSegmentId(params.get('segment'))
    }
    /// #endif
  }

  public setActor(actor: RunnerActor) {
    this.world.state.setActor(actor)
  }

  async load(abort: AbortSignal): Promise<void> {
    await Promise.all([
      this.audio.load(abort),
      this.renderer.load(abort),
      this.world.systems.get<SequencingSystem>(SequencingSystem).load(abort),
    ])
  }

  start() {
    this.glRenderer.cameras.getManager('main').setControler(this.camCtrl)

    this.world.state.setJauge(0)
    this.world.start()
  }

  preRender(): void {
    this.world.update(this.glRenderer.dt)
    const time = this.world.time
    this.renderer.preRender(time)
    this.root.updateWorldMatrix()
  }

  rttPass(context: RenderContext): void {
    this.renderer.rttPass(context)
  }

  render(context: RenderContext): void {
    this.renderer.render(context)
    this.world.systems.forEach((system) => system.render?.(context))

    // if (this.camCtrl.camera)
    // DebugDraw.drawFrustum(this.camCtrl.camera._viewProj);
  }

  dispose(): void {
    this.world.dispose()
    this.audio.dispose()
  }
}
